/**
 * FeedbackManagementPage - Comprehensive feedback management interface
 * 
 * Provides unified view of all feedback across applications with:
 * - Clickable feedback cards
 * - Modal detailed views
 * - Advanced filtering with localStorage persistence
 * - Real-time updates
 */
import React, { useState, useEffect, useCallback } from 'react';
import FeedbackTable from '../organisms/FeedbackTable';
import FeedbackDetails from '../organisms/FeedbackDetails';
import Modal from '../atoms/Modal';
import { useApiService } from '../../services/apiService';
import { useRealTimeFeedback } from '../../hooks/useRealTimeFeedback';
import useFilterPersistence from '../../hooks/useFilterPersistence';
import { FeedbackStatusType } from '../atoms/FeedbackStatus';
import { FeedbackPriorityType } from '../molecules/FeedbackCard';

// Types (matching the existing FeedbackTable component)
interface UserSummary {
  auth0_user_id: string;
  name?: string;
  email?: string;
  picture?: string;
}

interface AppSummary {
  client_id: string;
  name: string;
  description?: string;
  logo_uri?: string;
}

interface SentimentData {
  sentiment: 'positive' | 'negative' | 'neutral';
  confidence: number;
  themes: string[];
  summary: string;
  recommendations: string[];
  analyzed_at: string;
  model_version: string;
  context_analysis?: string;
}

interface FeedbackData {
  id: number;
  title: string;
  description: string;
  status: FeedbackStatusType;
  priority: FeedbackPriorityType;
  created_at: string;
  updated_at?: string;
  user?: UserSummary;
  app?: AppSummary;
  assigned_to?: string;
  assigned_user?: UserSummary;
  external_id?: string;
  source_url?: string;
  extra_data?: any;
  sentiment_analysis?: SentimentData;
}

interface FeedbackFilters {
  status?: FeedbackStatusType;
  priority?: FeedbackPriorityType;
  app_id?: string;
  assigned_to?: string;
  search?: string;
}

interface SortOption {
  field: 'created_at' | 'updated_at' | 'priority' | 'status';
  direction: 'asc' | 'desc';
}

interface PaginatedFeedbackList {
  items: FeedbackData[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface FeedbackManagementPageProps {
  className?: string;
}

const DEFAULT_FILTERS: FeedbackFilters = {
  status: undefined,
  priority: undefined,
  app_id: undefined,
  assigned_to: undefined,
  search: ''
};

const DEFAULT_SORT: SortOption = {
  field: 'created_at',
  direction: 'desc'
};

const FeedbackManagementPage: React.FC<FeedbackManagementPageProps> = ({
  className = ''
}) => {
  console.log('🎯 FeedbackManagementPage: Component rendering', {
    timestamp: new Date().toISOString(),
    className
  });
  // State management
  const [feedbackList, setFeedbackList] = useState<FeedbackData[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setSort] = useState<SortOption>(DEFAULT_SORT);
  
  // Modal state for detailed feedback view
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Services and hooks
  const apiService = useApiService();
  const { newFeedback, comments, loading: realtimeLoading } = useRealTimeFeedback();
  
  // Debug state changes
  useEffect(() => {
    console.log('📊 FeedbackManagementPage: State update', {
      feedbackListLength: feedbackList.length,
      total,
      loading,
      realtimeLoading,
      error,
      currentPage,
      totalPages,
      newFeedbackLength: newFeedback.length,
      commentsLength: comments.length
    });
  }, [feedbackList, total, loading, realtimeLoading, error, currentPage, totalPages, newFeedback, comments]);
  
  // Filter persistence
  const { 
    filters, 
    setFilters, 
    resetFilters, 
    isLoading: filtersLoading 
  } = useFilterPersistence({
    key: 'feedback-management',
    defaultFilters: DEFAULT_FILTERS
  });

  // Fetch feedback data
  const fetchFeedback = useCallback(async () => {
    if (!apiService || filtersLoading) {
      console.log('🔍 FeedbackManagementPage: Skipping fetch', { 
        hasApiService: !!apiService, 
        filtersLoading 
      });
      return;
    }
    
    console.log('🚀 FeedbackManagementPage: Starting feedback fetch', {
      currentPage,
      pageSize,
      filters,
      timestamp: new Date().toISOString()
    });
    
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: currentPage,
        per_page: pageSize,
        include_user: true,
        include_app: true,
        include_comments: false,
        ...(filters.status && { status: filters.status }),
        ...(filters.priority && { priority: filters.priority }),
        ...(filters.app_id && { app_id: filters.app_id }),
        ...(filters.assigned_to && { assigned_to: filters.assigned_to }),
        ...(filters.search && { search: filters.search })
      };

      console.log('📤 FeedbackManagementPage: API request params', params);

      const response = await apiService.getFeedbackList(params);
      const data = response.data as PaginatedFeedbackList;
      
      console.log('📥 FeedbackManagementPage: API response received', {
        itemsCount: data.items?.length || 0,
        total: data.total,
        page: data.page,
        pages: data.pages,
        hasNext: data.has_next,
        hasPrev: data.has_prev,
        items: data.items?.slice(0, 2) // Show first 2 items for debug
      });

      setFeedbackList(data.items);
      setTotal(data.total);
      setTotalPages(data.pages);
      
      console.log('✅ FeedbackManagementPage: State updated successfully', {
        feedbackCount: data.items?.length || 0,
        totalItems: data.total
      });
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('❌ FeedbackManagementPage: Error fetching feedback', {
        error: err,
        errorMessage,
        params: {
          currentPage,
          pageSize,
          filters
        }
      });
    } finally {
      setLoading(false);
      console.log('🏁 FeedbackManagementPage: Fetch completed', { 
        loading: false,
        timestamp: new Date().toISOString() 
      });
    }
  }, [apiService, currentPage, pageSize, filters, filtersLoading]);

  // Initial load and filter changes
  useEffect(() => {
    console.log('🔄 FeedbackManagementPage: useEffect triggered - fetchFeedback dependency changed', {
      timestamp: new Date().toISOString(),
      currentPage,
      pageSize,
      filters,
      filtersLoading,
      hasApiService: !!apiService
    });
    fetchFeedback();
  }, [fetchFeedback]);

  // Integrate real-time updates
  useEffect(() => {
    if (newFeedback.length > 0) {
      // Refresh the list when new feedback arrives
      fetchFeedback();
    }
  }, [newFeedback, fetchFeedback]);

  // Handle real-time comment updates
  useEffect(() => {
    if (comments.length > 0 && selectedFeedback) {
      // Check if any new comments are for the currently selected feedback
      const relevantComments = comments.filter(commentUpdate => 
        commentUpdate.feedback_id === selectedFeedback.id.toString()
      );
      
      if (relevantComments.length > 0) {
        // Refresh the detailed feedback to show the new comments
        const refreshDetailedFeedback = async () => {
          try {
            const response = await apiService.getFeedbackById(selectedFeedback.id, {
              include_comments: true,
              include_user: true,
              include_app: true
            });
            setSelectedFeedback(response.data as FeedbackData);
          } catch (err) {
            console.error('Error refreshing feedback details after comment update:', err);
          }
        };
        
        refreshDetailedFeedback();
      }
    }
  }, [comments, selectedFeedback, apiService]);

  // Handle feedback card click - open detailed modal
  const handleFeedbackClick = async (feedback: FeedbackData) => {
    setSelectedFeedback(feedback);
    setIsModalOpen(true);
    
    // Always fetch complete data including comments
    if (apiService) {
      setLoadingDetail(true);
      try {
        const response = await apiService.getFeedbackById(feedback.id, {
          include_comments: true,
          include_user: true,
          include_app: true
        });
        setSelectedFeedback(response.data as FeedbackData);
      } catch (err) {
        console.error('Error fetching detailed feedback:', err);
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedFeedback(null);
    setLoadingDetail(false);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: FeedbackFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Handle sort changes
  const handleSortChange = (newSort: SortOption) => {
    setSort(newSort);
    setCurrentPage(1); // Reset to first page on sort change
  };

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle status/priority changes from detail modal
  const handleStatusChange = async (id: number, status: FeedbackStatusType) => {
    if (!apiService) return;
    
    try {
      await apiService.updateFeedback(id, { status });
      
      // Update local state
      setFeedbackList(prev => prev.map(feedback => 
        feedback.id === id ? { ...feedback, status } : feedback
      ));
      
      // Update selected feedback if it's the same one
      if (selectedFeedback?.id === id) {
        setSelectedFeedback(prev => prev ? { ...prev, status } : null);
      }
      
      // Refresh the list to get updated data
      fetchFeedback();
    } catch (err) {
      console.error('Error updating feedback status:', err);
    }
  };

  const handlePriorityChange = async (id: number, priority: FeedbackPriorityType) => {
    if (!apiService) return;
    
    try {
      await apiService.updateFeedback(id, { priority });
      
      // Update local state
      setFeedbackList(prev => prev.map(feedback => 
        feedback.id === id ? { ...feedback, priority } : feedback
      ));
      
      // Update selected feedback if it's the same one
      if (selectedFeedback?.id === id) {
        setSelectedFeedback(prev => prev ? { ...prev, priority } : null);
      }
      
      // Refresh the list to get updated data
      fetchFeedback();
    } catch (err) {
      console.error('Error updating feedback priority:', err);
    }
  };

  const handleAddComment = useCallback(async (feedbackId: number, content: string) => {
    if (!apiService) return;
    
    try {
      const response = await apiService.createComment({
        feedback_id: feedbackId,
        content,
        is_internal: false
      });

      console.log('Comment created successfully:', response.data);

      // Re-fetch the feedback detail so the new comment shows immediately.
      // We don't rely on the WebSocket real-time path alone since it may not
      // be connected; getFeedbackById returns the full, updated comment list.
      const refreshed = await apiService.getFeedbackById(feedbackId, {
        include_comments: true,
        include_user: true,
        include_app: true
      });
      setSelectedFeedback(prev =>
        prev && prev.id === feedbackId ? (refreshed.data as FeedbackData) : prev
      );
    } catch (err) {
      console.error('Error adding comment:', err);
      // You might want to show a toast notification here
    }
  }, [apiService]);
  

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Feedback Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage feedback across all applications
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={resetFilters}
              className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Reset Filters
            </button>
            <button
              onClick={fetchFeedback}
              disabled={loading}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Table */}
      <FeedbackTable
        feedbackList={feedbackList}
        total={total}
        loading={loading}
        error={error || undefined}
        onFeedbackClick={handleFeedbackClick}
        onPageChange={handlePageChange}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
        currentPage={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        showFilters={true}
        showPagination={true}
        compact={false}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6"
      />

      {/* Detailed Feedback Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        size="xl"
        showCloseButton={false}
        closeOnBackdropClick={true}
        closeOnEscape={true}
      >
        {selectedFeedback ? (
          <div className="p-6">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">
                  Loading detailed information...
                </span>
              </div>
            ) : (
              <FeedbackDetails
                feedback={selectedFeedback}
                onStatusChange={handleStatusChange}
                onPriorityChange={handlePriorityChange}
                onAddComment={handleAddComment}
                onClose={handleModalClose}
                className="border-0 shadow-none"
              />
            )}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            No feedback selected
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FeedbackManagementPage;